import { useState, useRef } from "react";
import { trpc } from "../hooks/trpc";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, ArrowRight } from "lucide-react";
import { Eyebrow, LabelLine } from "../components/brand";

type ImportType = "mitarbeiter" | "kunden" | "projekte";

export function ImportPage() {
  const [type, setType] = useState<ImportType>("mitarbeiter");
  const [preview, setPreview] = useState<any>(null);
  const [base64, setBase64] = useState<string>("");
  const [result, setResult] = useState<any>(null);
  const [fileName, setFileName] = useState<string>("");
  const fileRef = useRef<HTMLInputElement>(null);

  const previewMutation = trpc.import.preview.useMutation();
  const executeMutation = trpc.import.execute.useMutation();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);

    const buffer = await file.arrayBuffer();
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
    setBase64(b64);

    const data = await previewMutation.mutateAsync({ base64: b64, type });
    setPreview(data);
  };

  const handleImport = async () => {
    const data = await executeMutation.mutateAsync({ base64, type, skipDuplicates: true });
    setResult(data);
    setPreview(null);
  };

  return (
    <div className="animate-in">
      <div className="mb-10">
        <Eyebrow>Import · Excel-Stammdaten</Eyebrow>
        <h1
          className="text-2xl font-bold mt-4"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-foreground)" }}
        >
          Excel-Import
        </h1>
        <p
          className="text-xs mt-2"
          style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
        >
          Stammdaten aus Excel-Dateien importieren
        </p>
      </div>

      {/* Upload Card */}
      <div className="card p-6 mb-6">
        <div className="flex gap-4 items-end flex-wrap">
          <div>
            <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Datentyp</LabelLine>
            <select className="input w-48 mt-2" value={type} onChange={e => { setType(e.target.value as ImportType); setPreview(null); setResult(null); }}>
              <option value="mitarbeiter">Mitarbeiter</option>
              <option value="kunden">Kunden</option>
              <option value="projekte">Projekte</option>
            </select>
          </div>
          <div>
            <LabelLine as="label" style={{ marginBottom: 8, display: "block" }}>Excel-Datei</LabelLine>
            <div className="mt-2">
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
              <button onClick={() => fileRef.current?.click()} className="btn btn-secondary">
                <FileSpreadsheet size={14} strokeWidth={1.5} />
                {fileName || "Datei waehlen..."}
              </button>
            </div>
          </div>
        </div>
      </div>

      {previewMutation.isPending && (
        <div className="card p-8 text-center">
          <div
            className="w-6 h-6 rounded-full animate-spin mx-auto mb-3"
            style={{
              border: "2px solid rgba(0,163,224,0.3)",
              borderTopColor: "var(--color-primary)",
            }}
          />
          <p className="text-sm" style={{ color: "var(--color-muted)" }}>Datei wird analysiert...</p>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div className="animate-in">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="card p-4 flex items-center gap-3">
              <div
                className="w-10 h-10 flex items-center justify-center"
                style={{
                  border: "1px solid var(--color-brand-grey-mid, #C6C6C6)",
                  borderRadius: "var(--radius-sm)",
                  color: "var(--color-primary)",
                }}
              >
                <CheckCircle2 size={18} strokeWidth={1.5} />
              </div>
              <div>
                <div
                  className="text-xl font-bold"
                  style={{ color: "var(--color-foreground)", fontFamily: "var(--font-display)" }}
                >
                  {preview.totalRows}
                </div>
                <div
                  className="text-[10px]"
                  style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
                >
                  Zeilen gesamt
                </div>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <div
                className="w-10 h-10 flex items-center justify-center"
                style={{
                  border: "1px solid var(--color-brand-grey-mid, #C6C6C6)",
                  borderRadius: "var(--radius-sm)",
                  color: "var(--color-warning)",
                }}
              >
                <AlertTriangle size={18} strokeWidth={1.5} />
              </div>
              <div>
                <div
                  className="text-xl font-bold"
                  style={{ color: "var(--color-warning)", fontFamily: "var(--font-display)" }}
                >
                  {preview.duplicates}
                </div>
                <div
                  className="text-[10px]"
                  style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
                >
                  Duplikate
                </div>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <div
                className="w-10 h-10 flex items-center justify-center"
                style={{
                  border: "1px solid var(--color-brand-grey-mid, #C6C6C6)",
                  borderRadius: "var(--radius-sm)",
                  color: "var(--color-danger)",
                }}
              >
                <XCircle size={18} strokeWidth={1.5} />
              </div>
              <div>
                <div
                  className="text-xl font-bold"
                  style={{ color: "var(--color-danger)", fontFamily: "var(--font-display)" }}
                >
                  {preview.errors}
                </div>
                <div
                  className="text-[10px]"
                  style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
                >
                  Fehler
                </div>
              </div>
            </div>
          </div>

          {/* Preview Table */}
          <div className="card overflow-auto mb-6" style={{ maxHeight: "400px" }}>
            <table className="table">
              <thead><tr>
                <th>Zeile</th>
                {preview.headers.map((h: string) => <th key={h}>{h}</th>)}
                <th>Status</th>
              </tr></thead>
              <tbody>
                {preview.rows.slice(0, 50).map((row: any) => (
                  <tr key={row.rowNum} style={{ background: row.error ? "rgba(184,50,44,0.05)" : row.isDuplicate ? "rgba(184,117,0,0.05)" : "transparent" }}>
                    <td style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>{row.rowNum}</td>
                    {preview.headers.map((h: string) => <td key={h}>{row.data[h] ?? ""}</td>)}
                    <td>
                      {row.error && <span className="badge" style={{ background: "var(--color-danger-bg)", color: "var(--color-danger)" }}><XCircle size={10} strokeWidth={1.5} /> Fehler</span>}
                      {row.isDuplicate && <span className="badge" style={{ background: "var(--color-warning-bg)", color: "var(--color-warning)" }}><AlertTriangle size={10} strokeWidth={1.5} /> Duplikat</span>}
                      {!row.error && !row.isDuplicate && <span className="badge" style={{ background: "var(--color-success-bg)", color: "var(--color-success)" }}><CheckCircle2 size={10} strokeWidth={1.5} /> OK</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button onClick={handleImport} disabled={executeMutation.isPending} className="btn btn-success group">
            {executeMutation.isPending ? (
              <>
                <div
                  className="w-4 h-4 rounded-full animate-spin"
                  style={{ border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white" }}
                />{" "}
                Importiert...
              </>
            ) : (
              <>
                <Upload size={14} strokeWidth={1.5} />
                {preview.totalRows - preview.duplicates - preview.errors} Eintraege importieren
                <ArrowRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="card p-6 animate-in">
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-11 h-11 flex items-center justify-center"
              style={{
                border: "1px solid var(--color-brand-grey-mid, #C6C6C6)",
                borderRadius: "var(--radius-sm)",
                color: "var(--color-success)",
              }}
            >
              <CheckCircle2 size={20} strokeWidth={1.5} />
            </div>
            <h3
              className="text-lg font-bold"
              style={{ color: "var(--color-success)", fontFamily: "var(--font-display)" }}
            >
              Import abgeschlossen
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className="text-2xl font-bold" style={{ color: "var(--color-foreground)", fontFamily: "var(--font-display)" }}>
                {result.imported}
              </span>
              <br />
              <span
                className="text-[10px]"
                style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
              >
                Importiert
              </span>
            </div>
            <div>
              <span className="text-2xl font-bold" style={{ color: "var(--color-warning)", fontFamily: "var(--font-display)" }}>
                {result.skipped}
              </span>
              <br />
              <span
                className="text-[10px]"
                style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
              >
                Uebersprungen
              </span>
            </div>
            <div>
              <span className="text-2xl font-bold" style={{ color: "var(--color-danger)", fontFamily: "var(--font-display)" }}>
                {result.errors.length}
              </span>
              <br />
              <span
                className="text-[10px]"
                style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
              >
                Fehler
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
